import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface CartItem {
  cartId: string; // unique per product + size + color
  id: string; // product id
  name: string;
  price: number;
  image_url: string | null;
  quantity: number;
  stock: number;
  size?: string | null;
  color?: string | null;
}

const makeCartId = (productId: string, size?: string | null, color?: string | null) =>
  `${productId}${size ? `::s:${size}` : ""}${color ? `::c:${color}` : ""}`;

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  addItem: (item: Omit<CartItem, "quantity" | "cartId">, qty?: number) => void;
  removeItem: (cartId: string) => void;
  updateQty: (cartId: string, qty: number) => void;
  clear: () => void;
  setOpen: (open: boolean) => void;
  subtotal: () => number;
  count: () => number;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      addItem: (item, qty = 1) => {
        const cartId = makeCartId(item.id, item.size, item.color);
        const existing = get().items.find((i) => i.cartId === cartId);
        if (existing) {
          set({
            items: get().items.map((i) =>
              i.cartId === cartId ? { ...i, quantity: Math.min(i.quantity + qty, item.stock) } : i,
            ),
          });
        } else {
          set({ items: [...get().items, { ...item, cartId, quantity: qty }] });
        }
      },
      removeItem: (cartId) => set({ items: get().items.filter((i) => i.cartId !== cartId) }),
      updateQty: (cartId, qty) =>
        set({
          items: get().items.map((i) =>
            i.cartId === cartId ? { ...i, quantity: Math.max(1, Math.min(qty, i.stock)) } : i,
          ),
        }),
      clear: () => set({ items: [] }),
      setOpen: (open) => set({ isOpen: open }),
      subtotal: () => get().items.reduce((s, i) => s + i.price * i.quantity, 0),
      count: () => get().items.reduce((s, i) => s + i.quantity, 0),
    }),
    {
      name: "rupshoili-cart",
      version: 3,
      migrate: (persisted: any) => {
        if (!persisted) return persisted;
        if (Array.isArray(persisted.items)) {
          persisted.items = persisted.items.map((i: any) => ({
            ...i,
            size: i.size ?? null,
            color: i.color ?? null,
            cartId: makeCartId(i.id, i.size ?? null, i.color ?? null),
          }));
        }
        return persisted;
      },
    },
  ),
);
